import React, {
  useState,
  useReducer,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { useFormContext, Controller, RegisterOptions } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { useDebouncedCallback } from 'use-debounce';
import clsx from 'clsx';

import { InputTextField } from '@marriott/mi-ui-library';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  phoenixLegalHotelSuggestionQuery,
  phoenixLegalVisitHotelWebsiteQuery,
  phoenixLegalContactUsTopicsAndSubTopicsQuery,
} from '@marriott/mi-legal-graphql';

import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';

import { useAuthStore, useLegalGenericStore } from '../../store';

import { TypeAndSearchWithLink } from '../TypeAndSearchWithLink';
import { SingleDayPicker } from '../SingleDayPicker';
import { LegalTextArea } from '../LegalTextArea';
import { ToggleRadioButtons } from '../ToggleRadioButtons';
import { Dropdown } from '../Dropdown';
import { LegalTextWithLink } from '../LegalTextWithLink';

import {
  HotelBookInformationState,
  HotelBookInformationActions,
  ComplimentConcernType,
} from './reducer/hotelBookInformationReducer.types';
import { hotelInformationInitialState, hotelBookInformationReducer } from './reducer/hotelBookInformationReducer';
import {
  HotelBookInformationProps,
  HotelBookInformationCustomResetRef,
  SubTopicValueType,
  SubTopicListType,
  DefaultSubTopicValueType,
  HotelWebsiteLinkDataType,
  PhoenixLegalHotelSuggestionQueryResult,
  PhoenixLegalVisitHotelWebsiteQuery,
} from './HotelBookInformation.types';
import { TOPIC_LIST_FOR_SUBTOPICS } from './constants';
import { processHotelSearchResultsWithRequiredFields, formatHotelFieldsWithRequiredLabels } from './helper';
import { StyledHotelBookInformation } from './HotelBookInformation.styles';
import {
  TopicValueType,
  PhoenixLegalContactUsTopicsAndSubTopicsType,
} from '../../organisms/ContactUsForm/ContactUsForm.types';

const GET_HOTELS_SEARCH_RESULTS_OPERATION_NAME = 'phoenixLegalHotelSuggestionQuery';
const GET_HOTELS_WEBSITE_OPERATION_NAME = 'phoenixLegalVisitHotelWebsiteQuery';
const GET_TOPICS_AND_SUBTOPICS_OPERATION_NAME = 'phoenixLegalContactUsTopicsAndSubTopicsQuery';

const HotelBookInformationComponent: ForwardRefRenderFunction<
  HotelBookInformationCustomResetRef,
  HotelBookInformationProps
> = (
  {
    selectedTopic,
    typeAndSearchWithLink,
    checkInDate,
    checkOutDate,
    confirmationNumber,
    comments,
    isAuth,
    complimentRadioButtonLabel,
    concernRadioButtonLabel,
    subTopicLabel,
    hotelServiceDescriptionText,
    hotelCreditCardReservationsText,
    commentsStillHaveAQuestionText,
    marriottBonvoyAccountCombineMultipleAccountsText,
    marriottBonvoyAccountEarningText,
    complimentConcernFoodAndBeverageSubTopicCommentsPlaceholder,
    complimentConcernStaffFeedbackSubTopicCommentsPlaceholder,
    optionFieldText,
    weekDaysShort,
    weekDaysLong,
    months,
    subTopicDropDownPlaceholder,
    showHideContactAndBtnContainer,
    resetForm,
  },
  ref
) => {
  const [hotelBookInformationStates, dispatch] = useReducer<
    React.Reducer<HotelBookInformationState, HotelBookInformationActions>
  >(hotelBookInformationReducer, hotelInformationInitialState);

  const [marriottBonvoyAccountLabelText, setMarriottBonvoyAccountLabelText] = useState<string | null>(null);
  const [subTopicValue, setSubTopicValue] = useState<SubTopicValueType>('GRF');
  const [hotelWebsiteLinkData, setHotelWebsiteLinkData] = useState<HotelWebsiteLinkDataType>({
    isWebSiteLinkLoading: false,
    websiteLink: '',
  });
  const [isSubTopicLoading, setIsSubTopicLoading] = useState(false);

  const {
    control,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = useFormContext();

  const { sessionId } = useAuthStore();
  const { isAuthorMode, language } = useLegalGenericStore();

  const {
    matchedHotelsResult,
    matchedHotelsPropertyId,
    showAboutAStayOption,
    showTypeAndSearchWithLink,
    showTypeAndSearchLink,
    showHotelBookInfoSection,
    showHotelServiceDescriptionText,
    showHotelCreditCardReservationsText,
    showComments,
    showCommentsStillHaveAQuestionText,
    complimentConcernValue,
    showComplimentConcernAndWebsiteSupportAndMarriottBonvoySubTopic,
    showMarriottBonvoyAccountLabelWithLinkText,
    subTopicsList,
  } = hotelBookInformationStates;

  const [getHotelList] = useLazyQuery<PhoenixLegalHotelSuggestionQueryResult>(phoenixLegalHotelSuggestionQuery, {
    context: generateApolloClientHeadersForUXLCalls(sessionId, GET_HOTELS_SEARCH_RESULTS_OPERATION_NAME, language),
    fetchPolicy: 'no-cache',
  });

  const [getSelectedHotelWebsite] = useLazyQuery<PhoenixLegalVisitHotelWebsiteQuery>(
    phoenixLegalVisitHotelWebsiteQuery,
    {
      context: generateApolloClientHeadersForUXLCalls(sessionId, GET_HOTELS_WEBSITE_OPERATION_NAME),
    }
  );

  const [getSubTopicsList] = useLazyQuery<PhoenixLegalContactUsTopicsAndSubTopicsType>(
    phoenixLegalContactUsTopicsAndSubTopicsQuery,
    {
      context: generateApolloClientHeadersForUXLCalls(sessionId, GET_TOPICS_AND_SUBTOPICS_OPERATION_NAME, language),
    }
  );

  const fetchSubTopicsList = useCallback(() => {
    setIsSubTopicLoading(true);

    getSubTopicsList({
      variables: {
        type: 'CONTACT_US_SUB_TOPICS',
      },
      onCompleted: subTopicsData => {
        const filteredSubTopicsWithOutEmptyKeys = subTopicsData?.contentListsByType
          .filter(t => Boolean(t.key.trim()))
          .slice()
          .reverse()
          .map(t => ({
            label: t.value,
            value: t.key as SubTopicValueType,
            linkedTopic: t.description as TopicValueType,
          }));
        dispatch({ type: 'SET_SUBTOPICS_LIST', payload: filteredSubTopicsWithOutEmptyKeys });
        setIsSubTopicLoading(false);
      },
      onError: error => {
        console.log(`Something went wrong while fetching  Sub topics data: ${JSON.stringify(error)}`);
        setIsSubTopicLoading(false);
      },
    });
  }, [getSubTopicsList]);

  useEffect(() => {
    selectedTopic === 'MRMAP' && setMarriottBonvoyAccountLabelText(marriottBonvoyAccountCombineMultipleAccountsText);
    dispatch({ type: 'SET_SELECTED_TOPIC', payload: selectedTopic });

    if (TOPIC_LIST_FOR_SUBTOPICS.includes(selectedTopic)) {
      fetchSubTopicsList();
    }
  }, [selectedTopic, marriottBonvoyAccountCombineMultipleAccountsText, fetchSubTopicsList]);

  useEffect(() => {
    const subTopicValue = getValues('subTopic');
    setValue('subTopicDescription', subTopicsList?.find(t => t.value === subTopicValue)?.label);
  }, [showComplimentConcernAndWebsiteSupportAndMarriottBonvoySubTopic, subTopicsList, getValues, setValue]);

  const handleHotelSearch = (searchKeyword: string) => {
    if (searchKeyword.length < 3) {
      return;
    }

    if (!isAuthorMode) {
      getHotelList({
        variables: {
          search: {
            filters: {
              suggestionTypes: 'PROPERTY',
            },
            query: searchKeyword,
          },
        },
        onCompleted: searchHotelsData => {
          const { suggestedPlacesBySearchString: { edges: hotelSearchNodes = [] } = {} } = searchHotelsData;
          const searchedHotelsResults = processHotelSearchResultsWithRequiredFields(hotelSearchNodes);

          console.log(searchedHotelsResults);
          dispatch({
            type: 'SET_HOTEL_SEARCH_KEYWORD',
            payload:
              searchedHotelsResults.length > 0
                ? {
                    hotelNames: searchedHotelsResults.map(formatHotelFieldsWithRequiredLabels),
                    hotelPropertyCodes: searchedHotelsResults.map(res => res.hotelPropertyId),
                  }
                : null,
          });
        },
        onError: error => {
          console.error(`Something went wrong while process hotel search results: ${JSON.stringify(error)}`);
        },
      });
    }
  };

  const getHotelWebsite = (hotelPropertyCode?: string) => {
    if (!isAuthorMode && hotelPropertyCode && (selectedTopic === 'HI' || selectedTopic === 'CCAF')) {
      setHotelWebsiteLinkData(prev => ({
        ...prev,
        isWebSiteLinkLoading: true,
      }));
      getSelectedHotelWebsite({
        variables: {
          version: 'V2',
          propertyId: hotelPropertyCode,
        },
        onCompleted: hotelWebsiteData => {
          if (hotelWebsiteData) {
            const { property: { basicInformation } = {} } = hotelWebsiteData;

            if (basicInformation?.websiteUrl) {
              setHotelWebsiteLinkData({
                isWebSiteLinkLoading: false,
                websiteLink: basicInformation.websiteUrl,
              });
              setValue('hwsButtonURL', basicInformation.websiteUrl);
            }
          }
        },
        onError: error => {
          setHotelWebsiteLinkData({
            isWebSiteLinkLoading: false,
            websiteLink: '',
          });
          console.error(`Something went wrong in hotel Website Data: ${JSON.stringify(error)}`);
        },
      });
    }
  };

  const debouncedHandleHotelSearch = useDebouncedCallback((val: string) => {
    handleHotelSearch(val);
  }, 500);

  const handleComplimentConcernValueChange = (value: ComplimentConcernType) => {
    dispatch({ type: 'SET_COMPLIMENT_CONCERN_VALUE', payload: value });
    value === 'concern' && fetchSubTopicsList();
    setSubTopicValue('GRF');
    showHideContactAndBtnContainer(true);
  };

  const handleSubTopicDropDownChange = (selectedTopic: TopicValueType, value: SubTopicValueType) => {
    if (selectedTopic === 'MRMAP') {
      const isCMMBA = value === 'CMMBA';

      showHideContactAndBtnContainer(!isCMMBA);

      let marriottBnvyLabelText;
      if (isCMMBA) {
        marriottBnvyLabelText = marriottBonvoyAccountCombineMultipleAccountsText;
      } else if (value === 'ERN') {
        marriottBnvyLabelText = marriottBonvoyAccountEarningText;
      } else {
        marriottBnvyLabelText = null;
      }

      setMarriottBonvoyAccountLabelText(marriottBnvyLabelText);
      dispatch({ type: 'MARRIOTT_BONVOY_SUBTOPIC_DROPDOWN_CHANGE', payload: value });
    }

    const currentTopicValue = getValues('subTopic');
    resetForm();
    setValue('subTopic', currentTopicValue);
  };

  const hotelNameValidation: { [key: string]: RegisterOptions } = {
    hotelName: {
      validate: value => value.trim() !== '' || typeAndSearchWithLink.searchFieldLabel.errorLabel,
    },
    comments: {
      validate: value => value.trim() !== '' || comments.errorLabel,
    },
    checkIn: {
      required: checkInDate.errorLabel,
    },
    checkout: {
      required: checkOutDate.errorLabel,
    },
  };

  const isCheckinAndCheckoutIsOptional = (selectedTopic: TopicValueType) => {
    return selectedTopic === 'GEMT' || selectedTopic === 'GMF';
  };

  const getRulesForCheckInAndCheckOutField = (selectedTopic: TopicValueType, fieldName: 'checkIn' | 'checkout') => {
    if (isCheckinAndCheckoutIsOptional(selectedTopic)) {
      return {};
    }
    return hotelNameValidation[fieldName];
  };

  const resetFormRenderedUI = (shouldNotReset: boolean) => {
    dispatch({
      type: 'RESET_FORM_RENDERED_UI',
      payload: { selectedTopicType: selectedTopic, shouldRenderUI: !shouldNotReset },
    });

    if (selectedTopic === 'HI') {
      showHideContactAndBtnContainer(!shouldNotReset);
    } else if (selectedTopic === 'MRMAP') {
      setMarriottBonvoyAccountLabelText(marriottBonvoyAccountCombineMultipleAccountsText);

      setTimeout(() => {
        showHideContactAndBtnContainer(false);
      }, 0);
    }
  };

  useImperativeHandle(ref, () => ({
    resetFormRenderedUI: () => {
      resetFormRenderedUI(true);
      selectedTopic === 'CCAPHS' && setSubTopicValue('GRF');
    },
  }));

  const getWebsiteSupportSubTopics = (subTopicsList: { label: string; value: string }[], isAuth: boolean) => {
    if (isAuth) {
      return subTopicsList;
    }

    return subTopicsList.filter(t => t.value !== 'MBATE');
  };

  const getSubTopicValueForSelectedTopic = (
    currentSelectedTopic: TopicValueType,
    subTopicsList: SubTopicListType | null,
    isAuth: boolean
  ) => {
    if (!subTopicsList) {
      return [{ label: subTopicDropDownPlaceholder, value: 'NA' }];
    }

    const list = subTopicsList
      .filter(s => s.linkedTopic === currentSelectedTopic)
      .map(s => ({
        label: s.label,
        value: s.value as string,
      }));

    if (selectedTopic === 'MS') {
      return getWebsiteSupportSubTopics(list, isAuth);
    }

    return list;
  };

  const getDefaultSubTopicValue = (currentSelectedTopic: TopicValueType, isAuth: boolean): DefaultSubTopicValueType => {
    switch (currentSelectedTopic) {
      case 'MS':
        return isAuth ? 'MBATE' : 'BWLU';
      case 'MRMAP':
        return 'CMMBA';
      default:
        return 'GRF';
    }
  };

  let commentsPlaceholder = '';
  if (showAboutAStayOption && complimentConcernValue === 'concern') {
    subTopicValue === 'SF' && (commentsPlaceholder = complimentConcernStaffFeedbackSubTopicCommentsPlaceholder);
    subTopicValue === 'FBF' && (commentsPlaceholder = complimentConcernFoodAndBeverageSubTopicCommentsPlaceholder);
  }

  return (
    <StyledHotelBookInformation
      className="mt-5"
      data-component-name="m-legal-HotelBookInformation"
      data-testid="legal-HotelBookInformation"
    >
      {showAboutAStayOption && (
        <Controller
          name="aboutAStayOption"
          control={control}
          key={complimentConcernValue}
          defaultValue={complimentConcernValue}
          render={({ field }) => (
            <ToggleRadioButtons
              radiobuttonName="aboutAStayOption"
              value={complimentConcernValue}
              toggleRadioButtonsContainerClassName="compliment-concern-container mb-5"
              firstRadiobuttonId="contact-us-compliment"
              secondRadiobuttonId="contact-us-concern"
              firstRadiobuttonLabel={complimentRadioButtonLabel}
              secondRadiobuttonLabel={concernRadioButtonLabel}
              firstRadioValue="compliment"
              secondRadioValue="concern"
              firstRadioSetTabIndex={0}
              secondRadioSetTabIndex={0}
              firstRadioDataTestId="contact-us-compliment"
              secondRadioDataTestId="contact-us-concern"
              firstRadioTabIndexForInput={0}
              secondRadioTabIndexForInput={0}
              firstRadioAriaLabelForLink="contact-us-compliment"
              secondRadioAriaLabelForLink="contact-us-concern"
              onChange={value => {
                handleComplimentConcernValueChange(value as ComplimentConcernType);
                resetForm();
                field.onChange({
                  target: {
                    name: field.name,
                    value,
                  },
                });
              }}
            />
          )}
        />
      )}

      {showComplimentConcernAndWebsiteSupportAndMarriottBonvoySubTopic && (
        <Controller
          name="subTopic"
          control={control}
          defaultValue={getDefaultSubTopicValue(selectedTopic, Boolean(isAuth))}
          key={getDefaultSubTopicValue(selectedTopic, Boolean(isAuth))}
          render={({ field }) => (
            <Dropdown
              label={subTopicLabel}
              options={getSubTopicValueForSelectedTopic(selectedTopic, subTopicsList, Boolean(isAuth))}
              name="subTopic"
              className={clsx('search-container mb-5', { 'skeleton-loader': isSubTopicLoading })}
              value={field.value}
              handleChange={selectedValue => {
                field.onChange({
                  target: {
                    name: field.name,
                    value: selectedValue,
                  },
                });

                handleSubTopicDropDownChange(selectedTopic, selectedValue as SubTopicValueType);
                setSubTopicValue(selectedValue as SubTopicValueType);
                setValue('subTopicDescription', subTopicsList?.find(t => t.value === selectedValue)?.label);
              }}
              id={'subTopic'}
            />
          )}
        />
      )}
      {showHotelServiceDescriptionText && <p>{hotelServiceDescriptionText}</p>}
      {showHotelCreditCardReservationsText && <p>{hotelCreditCardReservationsText}</p>}

      {showTypeAndSearchWithLink && (
        <Controller
          name="hotelName"
          control={control}
          rules={hotelNameValidation['hotelName']}
          defaultValue=""
          render={({ field }) => (
            <TypeAndSearchWithLink
              {...typeAndSearchWithLink}
              inputType="text"
              inputMaxLength={100}
              inputValue={field.value}
              testId="hotel_name"
              infoLabel="hotel_search_result"
              name="hotelName"
              isLinkRequired={showTypeAndSearchLink}
              linkUrl={hotelWebsiteLinkData.websiteLink}
              isLinkDataLoading={hotelWebsiteLinkData.isWebSiteLinkLoading}
              linkLabel={typeAndSearchWithLink.linkLabel!}
              hotelSearchResults={matchedHotelsResult}
              showErrorMsg={!!errors?.['hotelName']}
              className="mb-5"
              getSearchValue={(val: string, shouldSearchKeywordAgain: boolean, index: number) => {
                field.onChange({
                  target: {
                    name: field.name,
                    value: val,
                  },
                });
                if (shouldSearchKeywordAgain) {
                  dispatch({ type: 'SET_HOTEL_SEARCH_KEYWORD', payload: null });
                  debouncedHandleHotelSearch(val);
                  selectedTopic === 'HI' && !val && resetForm();
                }
                !shouldSearchKeywordAgain && index > -1 && getHotelWebsite(matchedHotelsPropertyId?.[index]);
                resetFormRenderedUI(shouldSearchKeywordAgain);
              }}
            />
          )}
        />
      )}
      {showHotelBookInfoSection && (
        <section className="hotel-book-info-container">
          <Controller
            name="checkin"
            control={control}
            rules={getRulesForCheckInAndCheckOutField(selectedTopic, 'checkIn')}
            render={({ field }) => (
              <SingleDayPicker
                className="hotel-book-info-items"
                placeholder={checkInDate.label}
                label={
                  isCheckinAndCheckoutIsOptional(selectedTopic)
                    ? `${checkInDate.label} ${optionFieldText}`
                    : checkInDate.label
                }
                defaultDate={field.value}
                errorMessage={errors?.['checkin'] ? (errors['checkin']?.['message'] as string) : ''}
                dayPickerPros={{
                  fixedWeeks: true,
                  month: watch('checkin'),
                  fromMonth: moment().subtract(1, 'year').toDate(),
                  toMonth: moment().add(2, 'year').toDate(),
                  months: months,
                  weekdaysLong: weekDaysLong,
                  weekdaysShort: weekDaysShort,
                  disabledDays: {
                    before: moment().subtract(1, 'year').toDate(),
                    after: moment().add(2, 'year').toDate(),
                  },
                }}
                onDayClick={val => {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: val,
                    },
                  });
                }}
                getInputProps={() => ({ name: 'checkin' })}
              />
            )}
          />
          <Controller
            name="checkout"
            control={control}
            rules={getRulesForCheckInAndCheckOutField(selectedTopic, 'checkout')}
            render={({ field }) => (
              <SingleDayPicker
                className="hotel-book-info-items"
                placeholder={checkOutDate.label}
                label={
                  isCheckinAndCheckoutIsOptional(selectedTopic)
                    ? `${checkOutDate.label} ${optionFieldText}`
                    : checkOutDate.label
                }
                defaultDate={field.value}
                disabled={!watch('checkin')}
                errorMessage={errors?.['checkout'] ? (errors['checkout']?.['message'] as string) : ''}
                dayPickerPros={{
                  fixedWeeks: true,
                  month: watch('checkout') == null ? moment(watch('checkin')).add(1, 'd').toDate() : watch('checkout'),
                  fromMonth: watch('checkin'),
                  toMonth: moment().add(2, 'year').toDate(),
                  months: months,
                  weekdaysLong: weekDaysLong,
                  weekdaysShort: weekDaysShort,
                  disabledDays: {
                    before: moment(watch('checkin')).add(1, 'd').toDate(),
                    after: moment().add(2, 'year').toDate(),
                  },
                }}
                onDayClick={val => {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: val,
                    },
                  });
                }}
                getInputProps={() => ({ name: 'checkout' })}
              />
            )}
          />
          <Controller
            name="confirmationNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <InputTextField
                className="m-input-field hotel-book-info-items single"
                label={confirmationNumber.label}
                type="text"
                testId="confirmation-number"
                inputValue={field.value}
                showErrorMessage={false}
                messageClass="mx-3"
                inputMaxLength={50}
                infoLabel="confirmation-number"
                getInputValue={(val: string) => {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: val,
                    },
                  });
                }}
                getInputProps={() => ({
                  autoComplete: 'off',
                  name: 'confirmationNumber',
                })}
              />
            )}
          />
        </section>
      )}
      {showCommentsStillHaveAQuestionText && <h4 className="mb-4">{commentsStillHaveAQuestionText}</h4>}
      {showMarriottBonvoyAccountLabelWithLinkText && marriottBonvoyAccountLabelText && (
        <LegalTextWithLink descriptionText={marriottBonvoyAccountLabelText} />
      )}
      {showComments && (
        <Controller
          name="comments"
          control={control}
          rules={hotelNameValidation['comments']}
          defaultValue=""
          render={({ field }) => (
            <LegalTextArea
              textareaName="comments"
              className="contactus-comments"
              textareaId="contactus_comments"
              textareaLabel={comments.label}
              isError={!!errors?.['comments']}
              errorMessage={errors?.['comments'] ? (errors['comments']?.['message'] as string) : ''}
              inputValue={field.value}
              placeholder={commentsPlaceholder}
              getInputValue={val => {
                field.onChange({
                  target: {
                    name: field.name,
                    value: val,
                  },
                });
              }}
            />
          )}
        />
      )}
    </StyledHotelBookInformation>
  );
};

export const HotelBookInformation = forwardRef(HotelBookInformationComponent);
